import $ from 'jquery';

import AccountAddressViewHelper from 'chairisher/view/helper/accountaddress';
import AlerterView from 'chairisher/view/alerter';
import CheckoutSection from 'chairisher/view/checkout/checkoutsection';
import HistoryUtils from 'chairisher/util/history';
import PaymentRequestCheckout, {
    makePurchaseDataForExpressCheckout,
} from 'chairisher/view/checkout/paymentrequestcheckout';
import ProductModel from 'chairisher/model/product';
import ReceiptView from 'chairisher/view/checkout/receipt';

import stripeLoader from 'chairisher/thirdparty/stripe';
import zendeskWebWidgetInst from 'chairisher/component/zendesk';

import { trackAddToCart } from 'chairisher/analytics/cart';
import {
    reportTotalMismatchDuringFinalization,
    trackPaymentComplete,
    trackSubmitError,
} from 'chairisher/analytics/checkout';
import { getGenericPaymentError, getGenericSubmitErrorString } from 'chairisher/context/error';
import {
    getOfferType,
    getPurchaseObject,
    getPurchaseProductAtIndex,
    getPurchaseUrl,
    getValidationEndpointUrl,
    isOffer,
    isPreviousInvalidOffer,
    isSellerInitiatedOffer,
    setFarthestEditedStep,
    setPurchaseObject,
} from 'chairisher/context/purchase';
import { logError, TrackingExpressCheckoutOrigin } from 'chairisher/util/analytics';
import { handleAsyncError } from 'chairisher/util/logging';

/**
 * Overarching view for checkout - manages CheckoutSectionView instances.
 */
class CheckoutView {
    /**
     * @param {Object} views An object of views that create the checkout experience.
     */
    constructor(views) {
        this.confirmView = views.confirmView;
        this.expressCheckoutView = views.expressCheckoutView;
        this.offerQuantityAndPriceView = views.offerQuantityAndPriceView;
        this.offerShippingPartnersView = views.offerShippingPartnersView;
        this.paymentView = views.paymentView;
        this.productsView = views.productsView;
        this.shippingAddressView = views.shippingAddressView;

        /**
         * Instance of the Stripe JS SDK.
         *
         * @see https://stripe.com/docs/js
         */
        this.stripe = null;

        /**
         * Tracks where an express checkout was initiated from.
         * This will be null when there is no active express checkout.
         *
         * @type {null|string}
         */
        this.expressCheckoutOrigin = null;

        /**
         * Checkout section view instances in the order that the user progresses through them
         *
         * @type {Array.<CheckoutSectionView>}
         */
        this.views = Object.values(views)
            .filter((view) => !!view.getStepNumber)
            .sort((view1, view2) => view1.getStepNumber() - view2.getStepNumber());

        /**
         * Renders the receipt in the "Order Summary" and "Review & Confirm" sections.
         *
         * @type {ReceiptView}
         */
        this.receipt = new ReceiptView($('.js-receipt-container'), {
            updatePurchaseObject: () => this.updatePurchaseObject(),
        });

        /**
         * The most recent validate call; once the server responds this should be nulled out
         *
         * @type {$.Deferred}
         */
        this.mostRecentXhr = null;

        /**
         * The CheckoutSectionView that is currently editable
         *
         * @type {CheckoutSectionView}
         */
        [this.editableView] = this.views;

        /**
         * @type {CheckoutSectionView}
         */
        this.farthestEditedView = this.editableView;
    }

    initialize() {
        this.receipt.bind();
        this.receipt.render();
        this.bind();

        if (this.paymentView) {
            stripeLoader().then((stripe) => {
                this.stripe = stripe;
                this.paymentView.initializeStripeElements();
                this.paymentView.mountStripeElements();
                this.initializePaymentRequestButtons();
            }, handleAsyncError);
        }

        this.views.forEach((view) => {
            // Callbacks that allow views to access data outside their normal scope.
            view.setCallbacks({
                editStep: (step, shouldForceReset) => this.editStep(step, shouldForceReset),
                getCurrentStepNumber: () => this.editableView.getStepNumber(),
                getStripe: () => this.stripe,
                setDefaultSubmitButtonText: () => this.confirmView.setDefaultSubmitButtonText(),
                setPaymentRequestButton: () => this.confirmView.setPaymentRequestButton(),
                updateEditableView: (shouldRenderErrors) => this.updateEditableView(shouldRenderErrors),
                updatePurchaseObject: () => this.updatePurchaseObject(),
            });
            view.initialize();
        });

        if (this.expressCheckoutView) {
            this.expressCheckoutView.setExpressCheckoutCallback(() => {
                this.expressCheckoutOrigin = TrackingExpressCheckoutOrigin.CART;
                this.expressCheckout();
            });
            this.expressCheckoutView.initialize();
        }
        if (this.doesContextHavePaypalExpressCheckoutData()) {
            this.expressCheckoutOrigin = TrackingExpressCheckoutOrigin.MINICART;
            this.expressCheckout();
        } else if (isPreviousInvalidOffer()) {
            this.expressCheckout();
        } else {
            this.editStep(1);
        }

        zendeskWebWidgetInst.startAutoloadTimer();
    }

    initializePaymentRequestButtons() {
        if (this.expressCheckoutView && this.expressCheckoutView.getPaymentRequestButtonWrapper().length > 0) {
            // Initialize payment request button in express checkout section
            const paymentRequest = new PaymentRequestCheckout({
                buttonHeight: '36px',
                buttonSelector: '.js-express-payment-request-button-wrapper',
                buttonType: 'default',
                expressCheckoutOrigin: TrackingExpressCheckoutOrigin.CART,
                getPurchaseData: () => {
                    const purchaseData = getPurchaseObject();
                    const { products } = purchaseData;
                    const [product] = products;

                    // Initialize express checkout with a subset of the actual purchase data since
                    // the shipping address from the payment request UI is different.
                    return makePurchaseDataForExpressCheckout({
                        initialTotal: purchaseData.meta.purchase_total.value,
                        productId: product.id.value,
                        productPrice: product.price.value,
                        quantity: product.quantity.value,
                        numProducts: products.length,
                    });
                },
                isExpressCheckout: true,
                purchaseUrl: getPurchaseUrl(),
                stripe: this.stripe,
            });

            paymentRequest.initialize().then(() => {
                if (paymentRequest.isAvailable() && paymentRequest.isEligibleForExpressCheckout()) {
                    this.expressCheckoutView.getPaymentRequestButtonWrapper().removeClass('hidden');
                }
            }, handleAsyncError);
        }

        const $paymentRequestWrapper = this.paymentView.getPaymentRequestWrapper();
        if ($paymentRequestWrapper.length > 0) {
            // Initialize payment request button in payment section
            const paymentRequest = new PaymentRequestCheckout({
                buttonType: isOffer() ? 'check-out' : 'buy',
                buttonSelector: '#js-card-review-and-confirm .js-payment-request-button-wrapper',
                getPurchaseData: () => getPurchaseObject(),
                purchaseUrl: getValidationEndpointUrl(),
                stripe: this.stripe,
            });

            paymentRequest.initialize().then(() => {
                if (paymentRequest.isAvailable()) {
                    // Make sure to only call this once.
                    paymentRequest.trackAvailability();

                    // Options are mutually exclusive dependent on browser; remove the unused logo.
                    if (paymentRequest.isApplePayAvailable()) {
                        $paymentRequestWrapper.find('.js-google-pay-mark').remove();
                    } else if (paymentRequest.isGooglePayAvailable()) {
                        $paymentRequestWrapper.find('.js-apple-pay-mark').remove();
                    }

                    // Set the radio value in payment card to whatever the available wallet is
                    this.paymentView.getPaymentRequestOption().val(paymentRequest.availableWalletName);
                    $paymentRequestWrapper.removeClass('hidden');
                }
            }, handleAsyncError);
        }
    }

    bind() {
        const $checkoutForm = $('.js-checkout-form');
        AccountAddressViewHelper.bindStreetAddressLine2BtnCollapse($checkoutForm);

        $('.js-buy-now-link').on('click', (e) => {
            e.preventDefault();
            trackAddToCart(new ProductModel(getPurchaseProductAtIndex(0)), $(e.currentTarget).data('position'));
            $('.js-buy-now-form').submit();
        });

        // Toggles whether the 'How offers work' section is collapsed on small view ports (create offer/counter offer)
        $('.js-how-click-target').on('click', () => {
            $('.js-how-collapsible-container, .js-how-collapse-toggle').toggleClass('hidden');
        });

        const $nextButtons = $('.js-next');
        $nextButtons.on('click', (e) => {
            e.preventDefault();
            this.editableView.setHasClickedNext(true);
            $nextButtons.prop('disabled', true);
            this.editNextStep().always(() => {
                $nextButtons.prop('disabled', false);
            });
        });

        $('.js-chairish-card-edit').on('click', (e) => {
            e.preventDefault();
            const $cardEl = $(e.currentTarget).closest('.js-chairish-card');
            this.editStep(CheckoutSection.getStepNumberFromCardEl($cardEl));
        });

        $checkoutForm.on('submit', (e) => {
            // Never allow the underlying checkout form to be actually submitted - it will 404.
            e.preventDefault();
        });

        if (this.paymentView) {
            this.paymentView.getPaymentMethodRadioButtons().on('change', () => {
                // If the user changes the payment method, then it's no longer an express checkout.
                this.expressCheckoutOrigin = null;
            });
        }

        this.confirmView.getSubmitButton().on('click', () => {
            this.finalize();
        });

        $('.js-back-link').on('click', (e) => {
            // This is for the "Back to shopping" link
            // Only use history if the referrer is a Chairish shopping page
            if (HistoryUtils.isBackPermitted() && /search|product|collection|shop/.test(document.referrer)) {
                window.history.back();
                e.preventDefault();
            }
        });

        // Binds chat widget link in footer
        $('.js-live-chat-widget').on('click', (e) => {
            e.preventDefault();
            zendeskWebWidgetInst.handleWindowLoad();
        });
    }

    /**
     * @returns {boolean} Whether the context has data set from a PayPal express checkout.
     */
    doesContextHavePaypalExpressCheckoutData() {
        const purchaseObject = getPurchaseObject();
        const paymentMethod = purchaseObject.payment_method;
        return !!(
            paymentMethod &&
            paymentMethod.braintree_device_data &&
            paymentMethod.braintree_nonce &&
            paymentMethod.braintree_nonce.value
        );
    }

    /**
     * Get the payment method value for use in analytics.
     *
     * @returns {string}
     */
    getPaymentMethodValue() {
        let value = 'none'; // This should never be the case, but setting as a fallback just in case.
        if (this.paymentView) {
            value = this.paymentView.getPaymentMethodValue();
        } else {
            // This is the case for counter offer.
            const paymentProcessor = getPurchaseObject().payment_method.payment_processor;
            if (paymentProcessor && paymentProcessor.value) {
                value = paymentProcessor.value;
            }
        }
        return value;
    }

    /**
     * Sets the braintree express checkout data to the payment view.
     */
    setBraintreeExpressCheckoutData() {
        const purchaseObject = getPurchaseObject();
        this.paymentView.setBraintreeData({
            deviceData: purchaseObject.payment_method.braintree_device_data,
            nonce: purchaseObject.payment_method.braintree_nonce.value,
        });
        this.paymentView.renderPaypalAccount(purchaseObject.account.email?.value);
        this.paymentView.selectBraintreeRadioButton();
    }

    /**
     * Submits the form and then attempts to progress to final section.
     */
    async expressCheckout() {
        this.shippingAddressView.updateFormData();
        if (this.productsView) {
            this.productsView.renderForm();
        } else if (this.offerShippingPartnersView) {
            this.offerShippingPartnersView.renderForm();
        }
        [this.farthestEditedView] = this.views;
        if (this.doesContextHavePaypalExpressCheckoutData()) {
            // Set braintree data before submitting form so that payment view will be valid.
            this.setBraintreeExpressCheckoutData();
        }

        return this.updatePurchaseObject().done(() => {
            let isValid = true;

            const { views } = this;

            views.forEach((view) => {
                this.editableView = view;
                view.updateFormData();
                view.renderForm();
                view.setValueJSON();

                if (!isValid) {
                    // One of the previous views was invalid.
                    view.collapse();
                } else {
                    // All previous views are valid to this point.
                    this.farthestEditedView = view;
                    if (view === this.views[views.length - 1]) {
                        // Reached the last view, done.
                        view.edit();
                    } else if (view.isValid()) {
                        // The view is valid - summarize it and move to the next.
                        view.summarize();
                        view.renderSummary();
                    } else {
                        // The view is invalid - edit it, and collapse the rest.
                        isValid = false;
                        view.renderErrors();
                        view.edit();
                        this.expressCheckoutOrigin = null;
                    }
                }
            });
        });
    }

    /**
     * @param {number} stepNumber
     * @param {boolean=} shouldForceRestart Clears all cached values when true
     */
    editStep(stepNumber, shouldForceRestart = false) {
        this.views.forEach((view) => {
            if (shouldForceRestart) {
                view.clearValueJSON();
            }
            const viewStepNumber = view.getStepNumber();
            if (viewStepNumber < stepNumber) {
                view.renderSummary();
                view.summarize();
            } else if (viewStepNumber === stepNumber) {
                view.clearErrors();
                view.renderForm();
                this.editableView = view;
                view.edit();
            } else {
                view.collapse();
            }
        });
    }

    /**
     * Submits the data from all the checkout views and updates the purchase data with the response.
     * This is used for both validation and finalizing a purchase.
     *
     * @param {boolean=} shouldFinalize Whether to attempt purchase finalization
     * @returns {$.Deferred}
     */
    submit(shouldFinalize = false) {
        const purchaseObject = getPurchaseObject();
        if (this.mostRecentXhr && this.mostRecentXhr.state() === 'pending') {
            this.mostRecentXhr.abort();
        }

        // Create the request body by recursively merging every CheckoutSectionView's value with $.extend
        const requestData = {};
        this.views.forEach((view) => $.extend(true, requestData, view.getValue()));

        if (requestData.payment_method) {
            requestData.payment_method.should_calculate_processing_fee =
                this.editableView.shouldCalculateProcessingFee || this.farthestEditedView.shouldCalculateProcessingFee;
        }

        if (shouldFinalize) {
            const params = new URLSearchParams(document.location.search);
            $.extend(true, requestData, {
                // Users implicitly agree to the TOS by clicking submit.
                purchase_details: { has_agreed_to_tosapp: { value: true } },
                meta: {
                    should_finalize_purchase: { value: true },
                    purchase_total: purchaseObject.meta.purchase_total,
                    is_seller_initiated: { value: isSellerInitiatedOffer() },
                    offer_type: { value: getOfferType() },
                    private_deal_id: { value: params.get('private_deal_id') },
                },
            });
        }

        const xhr = $.ajax({
            contentType: 'application/json',
            type: 'POST',
            url: getValidationEndpointUrl(),
            data: JSON.stringify(requestData),
        })
            .fail((error) => {
                if (error.statusText !== 'abort') {
                    if (shouldFinalize) {
                        AlerterView.error(getGenericPaymentError());
                    } else {
                        AlerterView.error(getGenericSubmitErrorString());
                    }
                    logError('purchase submit error', error);
                }
            })
            .always(() => {
                if (this.mostRecentXhr.state() !== 'pending') {
                    this.mostRecentXhr = null;
                }
            });

        this.mostRecentXhr = xhr;
        return xhr;
    }

    /**
     * Calls the submit method and updates the purchase object with the result.
     *
     * @returns {$.Deferred}
     */
    updatePurchaseObject() {
        setFarthestEditedStep(this.editableView.getStepNumber());

        return this.submit().done((purchaseObject) => {
            if (!purchaseObject.products.length) {
                window.location.reload();
            }

            setPurchaseObject(purchaseObject);

            // Only render receipt if price value is valid
            const priceValue = purchaseObject.products[0]?.price?.value;

            if (priceValue && Number(priceValue) > 0) {
                this.receipt.render();
            }
        });
    }

    /**
     * Updates the editable view's data and optionally renders errors.
     *
     * @param {boolean} shouldRenderErrors
     * @returns {$.Deferred}
     */
    updateEditableView(shouldRenderErrors) {
        const result = $.Deferred();
        this.editableView.beforeUpdate().done(() => {
            const shouldSubmit = this.editableView.isDirty();
            const xhr = shouldSubmit ? this.updatePurchaseObject() : $.Deferred().resolve();

            return xhr
                .done(() => {
                    this.editableView.updateFormData();
                    this.editableView.renderForm();
                    if (shouldRenderErrors) {
                        this.editableView.renderErrors();
                    }
                    if (shouldSubmit) {
                        this.farthestEditedView = this.editableView;
                    }
                })
                .always(() => result.resolve());
        });

        return result;
    }

    /**
     * Updates the editable view, and if it's valid, progresses to the next step in checkout.
     *
     * @returns {$.Deferred}
     */
    editNextStep() {
        return this.updateEditableView(true).done(() => {
            if (this.editableView.isValid()) {
                const currentStep = this.editableView.getStepNumber();
                const farthestEditedStep = this.farthestEditedView.getStepNumber();

                if (!this.editableView.isDirty() && currentStep < farthestEditedStep) {
                    // If the view wasn't submitted restore the user's progress.
                    this.editStep(farthestEditedStep);
                } else {
                    this.editableView.submitSuccessAnalytics();
                    this.editStep(this.editableView.getStepNumber() + 1);
                    this.farthestEditedView = this.editableView;
                }
            } else {
                this.editableView.submitErrorAnalytics();
            }
        });
    }

    /**
     * Attempts to finalize the purchase.
     * Redirects the user to the 'thank you' page on a successful purchase.
     *
     * @returns {$.Deferred}
     */
    finalize() {
        const currentSubmitButtonText = this.confirmView.getCurrentSubmitButtonText();
        this.confirmView.setSubmitButtonLoading();
        const paymentMethodValue = this.getPaymentMethodValue();

        return this.submit(true)
            .done((response) => {
                const successUrl = response.meta && response.meta.success_url;
                const finalizeErrors = response.meta.should_finalize_purchase.errors || [];

                if (successUrl) {
                    trackPaymentComplete(
                        paymentMethodValue,
                        parseFloat(this.receipt.getTotal()),
                        this.expressCheckoutOrigin,
                    );
                    if (!isSellerInitiatedOffer()) {
                        window.location.href = successUrl;
                    } else {
                        const fullSuccessUrl = new URL(successUrl, window.location.origin);
                        fullSuccessUrl.searchParams.set('is_seller_initiated', 1);
                        window.location.href = fullSuccessUrl.toString();
                    }
                } else {
                    reportTotalMismatchDuringFinalization(getPurchaseObject(), response, paymentMethodValue);

                    // Check if the server reported a failed payment during finalization attempt
                    if (this.paymentView) {
                        setPurchaseObject(response);
                        if (!this.paymentView.isValid()) {
                            this.paymentView.updateFormData();
                            this.paymentView.clearValueJSON();
                            this.editableView.collapse();
                            this.editableView = this.paymentView;
                            this.editableView.edit();
                            this.editableView.renderForm();
                            this.editableView.renderErrors();
                        }
                    }

                    const $reengageErrorModal = $('.js-reengage-error-modal');
                    if (
                        $reengageErrorModal.length > 0 &&
                        (this.offerQuantityAndPriceView?.isValid() ||
                            this.offerQuantityAndPriceView?.$fieldsEl.find('.js-price-input-immutable').length > 0)
                    ) {
                        // This case is for users on the counter/reactivate pages that reach an error.
                        // Generally the user will be unable to fix the error, so we pop a modal that redirects them
                        // back to the offer create page.
                        // However, sometimes a finalization error will be due to a user error with the price input.
                        // In this case, do not pop the modal, unless the price input is immutable.
                        $reengageErrorModal.modal('show');
                    } else if (finalizeErrors.length > 0) {
                        finalizeErrors.forEach(({ display }) => AlerterView.error(display));
                    } else {
                        AlerterView.error(getGenericPaymentError());
                    }
                    trackSubmitError(response, paymentMethodValue, !!this.expressCheckoutOrigin);
                }
            })
            .always(() => {
                this.confirmView.setSubmitButtonText(currentSubmitButtonText);
                this.confirmView.toggleSubmitButtonDisabled(false);
            });
    }
}

export default CheckoutView;
